import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, FileText, Car, Shield, Building2, DollarSign, RefreshCw, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { VinAnalyzeDialog } from "./CalibrationReport";

export default function CalibrationReportsList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reprocessing, setReprocessing] = useState(false);
  const [vinOpen, setVinOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/calibration-reports").then((r) => setRows(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const reprocessLegacy = async () => {
    if (!window.confirm("Regenerate full Calibration Reports for all body-shop submitted jobs that don't yet have one? This is safe to run multiple times (idempotent).")) return;
    setReprocessing(true);
    try {
      const r = await api.post("/estimate-analyzer/reprocess-legacy-jobs");
      const d = r.data;
      toast.success(`Reprocessed ${d.processed} legacy job(s) · ${d.already_had_report} already had reports · ${d.skipped_no_lines} skipped (no lines) · ${d.failed} failed`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reprocess failed");
    } finally {
      setReprocessing(false);
    }
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid="calibration-reports-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">Calibration Reports</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight">Calibration Reports</h1>
          <p className="text-sm text-slate-400 mt-1.5">AI-extracted OEM calibration requirements per estimate.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
            onClick={reprocessLegacy}
            disabled={reprocessing}
            data-testid="reprocess-legacy-btn"
            title="Regenerate reports for body-shop submissions made before the engine was unified"
          >
            {reprocessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Reprocess Legacy Jobs
          </Button>
          <Link to="/calibration-reports/new">
            <Button className="bg-blue-600 hover:bg-blue-500" data-testid="new-report-btn">
              <Plus className="w-4 h-4 mr-2" /> New from Estimate
            </Button>
          </Link>
          <Button
            className="bg-cyan-600 hover:bg-cyan-500"
            onClick={() => setVinOpen(true)}
            data-testid="new-from-vin-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" /> New from VIN
          </Button>
        </div>
      </div>

      <VinAnalyzeDialog
        open={vinOpen}
        onClose={() => setVinOpen(false)}
        existingReportId={null}
        onDone={() => { setVinOpen(false); load(); }}
      />

      {loading ? (
        <div className="text-center text-slate-500 p-12">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-16 text-center">
          <FileText className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <div className="text-sm text-slate-400">No reports yet.</div>
          <Link to="/calibration-reports/new">
            <Button variant="outline" className="mt-4 border-slate-700">Upload first estimate</Button>
          </Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((row) => (
            <Link
              key={row.id}
              to={`/calibration-reports/${row.id}`}
              data-testid={`report-row-${row.id}`}
              className="rounded-xl border border-slate-800 bg-[#0B1120] p-4 hover:border-blue-500/50 transition-colors block"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="text-sm font-semibold text-slate-200 truncate">{row.vehicle_label || "Vehicle"}</div>
                <Badge className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10px] font-mono">
                  ${(row.total_projected_price || 0).toFixed(0)}
                </Badge>
              </div>
              {row.vin && <div className="font-mono text-[10px] text-blue-300 truncate mb-1.5">{row.vin}</div>}
              <div className="space-y-1 text-[11px] text-slate-400">
                {row.body_shop && <div className="flex items-center gap-1.5 truncate"><Building2 className="w-3 h-3 shrink-0" />{row.body_shop}</div>}
                {row.insurance && <div className="flex items-center gap-1.5 truncate"><Shield className="w-3 h-3 shrink-0" />{row.insurance}{row.claim_number ? ` · #${row.claim_number}` : ""}</div>}
                {row.source_filename && <div className="flex items-center gap-1.5 truncate"><FileText className="w-3 h-3 shrink-0" />{row.source_filename}</div>}
              </div>
              <div className="mt-3 pt-2 border-t border-slate-800 flex items-center justify-between text-[10px] text-slate-500">
                <span>{row.created_by_name || "—"}</span>
                <span>{row.created_at ? new Date(row.created_at).toLocaleDateString() : ""}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
